// Floating action button — single contextual primary action, mobile-only.
// Lives bottom-right, above the bottom nav (bottom: 80px). 56×56 circle.
//
// Don't ship multiple FABs on the same screen. If a screen has no obvious
// primary action, no FAB. The purpose is to keep the most-frequent
// player or DM action permanently thumb-reachable in the *easy zone*.
//
// Common contexts:
// - Stats tab: opens Quick Add Journal
// - DM Party tab: opens Batch Buff / Batch Debuff / Whisper sheet
// - Combat tab: advance turn / end combat (contextual)
//
// Usage:
//   {isMobile && stats === activeTab && (
//     <MobileFAB icon="✏️" ariaLabel="Quick add journal entry"
//                onClick={() => setShowQuickAdd(true)} />
//   )}

import React from 'react';
import useReducedMotion from './useReducedMotion';
import { t, DUR_FAST, SPRING_SNAP } from './transitions';

export default function MobileFAB({ icon, ariaLabel, onClick, badge, style }) {
  const reduceMotion = useReducedMotion();

  // Press handlers no-op the visual transform when reduce-motion is on,
  // but the haptic / activation still fires. Imperative inline-style
  // mutation pattern preserved from the original to avoid re-rendering
  // the FAB on every press.
  //
  // pressUp ALWAYS resets (no reduce-motion early-return) so that if
  // the user toggles the system reduce-motion setting mid-press, the
  // FAB doesn't stay stuck at scale(0.94). The reset is idempotent
  // when reduce-motion is on (transform is already 1) so there's no
  // cost to running it unconditionally.
  const pressDown = (e) => {
    if (reduceMotion) return;
    e.currentTarget.style.transform = 'scale(0.94)';
    e.currentTarget.style.filter = 'brightness(1.15)';
  };
  const pressUp = (e) => {
    e.currentTarget.style.transform = 'scale(1)';
    e.currentTarget.style.filter = 'brightness(1)';
  };

  return (
    <button
      onClick={onClick}
      aria-label={ariaLabel}
      style={{
        position: 'fixed',
        bottom: 'calc(80px + env(safe-area-inset-bottom, 0px))',
        right: '16px',
        width: '56px',
        height: '56px',
        borderRadius: '50%',
        background: 'var(--ap, #6b3fa0)',
        color: 'white',
        border: 'none',
        boxShadow: '0 4px 16px rgba(0,0,0,0.4), 0 2px 6px rgba(107,63,160,0.4)',
        fontSize: '1.5rem',
        cursor: 'pointer',
        zIndex: 90,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        touchAction: 'manipulation',
        WebkitTapHighlightColor: 'transparent',
        transition: reduceMotion ? 'none' : t(['transform', 'filter'], DUR_FAST, SPRING_SNAP),
        ...style,
      }}
      onMouseDown={pressDown}
      onMouseUp={pressUp}
      onMouseLeave={pressUp}
      onTouchStart={pressDown}
      onTouchEnd={pressUp}
    >
      {icon}
      {badge > 0 && (
        <span
          style={{
            position: 'absolute',
            top: '-2px',
            right: '-2px',
            minWidth: '20px',
            height: '20px',
            padding: '0 5px',
            background: '#c92727',
            color: 'white',
            borderRadius: '10px',
            fontSize: '0.7rem',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid var(--bg2, #1e1624)',
            lineHeight: 1,
          }}
        >
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </button>
  );
}
